/**
 * AWS Bedrock LLM 서비스
 *
 * 브라우저에서 직접 AWS SDK를 사용하면 Webpack 호환 문제가 발생하므로,
 * 백엔드 프록시 서버(server/index.js)를 통해 LLM을 호출합니다.
 *
 * 보안: systemPrompt, maxTokens 등 민감한 파라미터는 빌드 시점에 암호화됩니다.
 * 프론트엔드는 암호화된 값을 그대로 전송하고, 백엔드에서 복호화합니다.
 * 복호화 키는 백엔드에만 존재하므로 프론트엔드에서는 복호화가 불가능합니다.
 */

import axios from 'axios'
import { AWS_CONFIG } from '@/config/aws'
import {
  ENCRYPTED_IDEA_PROMPT,
  ENCRYPTED_PROPOSAL_PROMPT,
  ENCRYPTED_LLM_CONFIG
} from '@/constants/encryptedPrompts'

// 백엔드 API 베이스 URL (개발/프로덕션 동일)
const API_BASE_URL = 'https://mapi.swing2app.co.kr'

// 백엔드 LLM 프록시 엔드포인트
const LLM_PROXY_ENDPOINT = `${API_BASE_URL}/api/llm/invoke`
const LLM_STREAM_ENDPOINT = `${API_BASE_URL}/api/llm/stream`

// 모의 응답 사용 여부 (백엔드 서버 없이 테스트할 때만 true로 설정)
const USE_MOCK = false

/**
 * 요청 타입 상수
 * 프론트엔드에서 이 타입과 함께 암호화된 프롬프트를 전송합니다.
 */
export const REQUEST_TYPES = {
  IDEA_ANALYSIS: 'idea_analysis',      // 아이디어 분석
  PROPOSAL_GENERATION: 'proposal'       // 기획서 생성
}

/**
 * 요청 타입에 따른 암호화된 프롬프트 반환
 */
function getEncryptedPrompt(requestType) {
  switch (requestType) {
    case REQUEST_TYPES.IDEA_ANALYSIS:
      return ENCRYPTED_IDEA_PROMPT
    case REQUEST_TYPES.PROPOSAL_GENERATION:
      return ENCRYPTED_PROPOSAL_PROMPT
    default:
      return ENCRYPTED_IDEA_PROMPT
  }
}

/**
 * Bedrock를 통해 LLM 호출 (스트리밍)
 *
 * 백엔드 프록시 서버를 통해 AWS Bedrock API를 호출합니다.
 * 스트리밍이 필요한 경우 SSE를 사용합니다.
 *
 * @param {Array} messages - 메시지 배열 [{role, content}]
 * @param {Object} options - 옵션
 * @param {string} options.requestType - 요청 타입 (REQUEST_TYPES 중 하나)
 * @param {Function} options.onChunk - 청크 콜백
 * @returns {Promise<Object>} 응답 객체
 */
export async function invokeBedrockStream(messages, options = {}) {
  const {
    requestType = REQUEST_TYPES.IDEA_ANALYSIS,
    onChunk = null
  } = options

  // 모의 응답 사용 (개발 테스트용)
  if (USE_MOCK) {
    return simulateStreamingResponse(messages, onChunk)
  }

  // 스트리밍 요청이 있으면 SSE 사용
  if (onChunk) {
    return invokeWithSSE(messages, { requestType, onChunk })
  }

  // 일반 요청: 백엔드 프록시를 통해 호출
  try {
    const payload = {
      messages: messages.filter(m => m.role !== 'system').map(m => ({
        role: m.role,
        content: m.content
      })),
      // 암호화된 파라미터 전송 (백엔드에서 복호화)
      encryptedPrompt: getEncryptedPrompt(requestType),
      encryptedConfig: ENCRYPTED_LLM_CONFIG,
      requestType
    }

    const response = await axios.post(LLM_PROXY_ENDPOINT, payload)

    if (response.data && response.data.content) {
      return {
        content: response.data.content,
        model: AWS_CONFIG.modelId,
        usage: response.data.usage || { inputTokens: 0, outputTokens: 0, totalTokens: 0 },
        stopReason: response.data.stopReason || 'end_turn'
      }
    }

    throw new Error('Invalid response from LLM proxy')
  } catch (error) {
    console.error('[BedrockService] API 에러:', error)
    throw new Error(`LLM 호출 실패: ${error.message}`)
  }
}

/**
 * SSE를 사용한 스트리밍 호출
 */
async function invokeWithSSE(messages, options) {
  const { requestType, onChunk } = options

  return new Promise((resolve, reject) => {
    const payload = {
      messages: messages.filter(m => m.role !== 'system').map(m => ({
        role: m.role,
        content: m.content
      })),
      // 암호화된 파라미터 전송 (백엔드에서 복호화)
      encryptedPrompt: getEncryptedPrompt(requestType),
      encryptedConfig: ENCRYPTED_LLM_CONFIG,
      requestType
    }

    // POST 요청으로 SSE 스트림 시작
    fetch(LLM_STREAM_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream'
      },
      body: JSON.stringify(payload)
    }).then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let fullContent = ''
      let buffer = ''

      function processStream() {
        reader.read().then(({ done, value }) => {
          if (done) {
            resolve({
              content: fullContent,
              model: AWS_CONFIG.modelId,
              usage: { inputTokens: 0, outputTokens: 0, totalTokens: 0 },
              stopReason: 'end_turn'
            })
            return
          }

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              if (data === '[DONE]') {
                continue
              }
              try {
                const parsed = JSON.parse(data)
                if (parsed.type === 'content' && parsed.text) {
                  fullContent += parsed.text
                  if (onChunk) {
                    onChunk(parsed.text)
                  }
                } else if (parsed.type === 'error') {
                  reject(new Error(parsed.message))
                  return
                }
              } catch (e) {
                // JSON 파싱 실패 무시
              }
            }
          }

          processStream()
        }).catch(reject)
      }

      processStream()
    }).catch(reject)
  })
}

/**
 * 개발 환경용 스트리밍 응답 시뮬레이션
 */
async function simulateStreamingResponse(messages, onChunk) {
  const userMessage = messages.find(m => m.role === 'user')?.content || ''

  // 아이디어 분석 응답 생성
  const mockResponse = generateMockAnalysis(userMessage)

  // 스트리밍 시뮬레이션
  const fullContent = JSON.stringify(mockResponse, null, 2)

  if (onChunk) {
    const chunks = splitIntoChunks(fullContent, 30)
    for (const chunk of chunks) {
      onChunk(chunk)
      await new Promise(resolve => setTimeout(resolve, 30))
    }
  }

  return {
    content: fullContent,
    model: AWS_CONFIG.modelId,
    usage: {
      inputTokens: Math.floor(userMessage.length / 4),
      outputTokens: Math.floor(fullContent.length / 4),
      totalTokens: Math.floor((userMessage.length + fullContent.length) / 4)
    },
    stopReason: 'end_turn'
  }
}

/**
 * 문자열을 청크로 분할
 */
function splitIntoChunks(str, chunkSize) {
  const chunks = []
  for (let i = 0; i < str.length; i += chunkSize) {
    chunks.push(str.substring(i, i + chunkSize))
  }
  return chunks
}

/**
 * 모의 아이디어 분석 생성
 */
function generateMockAnalysis(userMessage) {
  // 키워드 분석
  const keywords = userMessage.toLowerCase()
  const hasDelivery = keywords.includes('배달') || keywords.includes('음식')
  const hasHealth = keywords.includes('헬스') || keywords.includes('건강') || keywords.includes('운동')
  const hasShopping = keywords.includes('쇼핑') || keywords.includes('상품') || keywords.includes('판매')
  const hasBooking = keywords.includes('예약') || keywords.includes('예매')
  const hasChat = keywords.includes('채팅') || keywords.includes('메시지')
  const hasPayment = keywords.includes('결제') || keywords.includes('구매')

  // 기본 분석 결과
  const analysis = {
    problemStatement: '사용자의 아이디어를 바탕으로 효율적인 서비스 플랫폼을 구축합니다.',
    targetUsers: '서비스에 관심이 있는 일반 사용자 및 관련 사업자',
    coreFeatures: [
      { id: 'feature_1', name: '회원 관리', description: '회원가입, 로그인, 프로필 관리 기능' },
      { id: 'feature_2', name: '메인 서비스', description: '핵심 서비스 기능 제공' },
      { id: 'feature_3', name: '알림 시스템', description: '푸시 알림 및 인앱 알림' }
    ],
    optionalFeatures: [
      { id: 'feature_4', name: '고객 지원', description: '문의하기 및 FAQ 기능' }
    ],
    suggestedModules: [
      { moduleId: 'auth', reason: '회원 인증 및 관리를 위해 필요합니다.' },
      { moduleId: 'db_basic', reason: '데이터 저장 및 관리를 위해 필요합니다.' },
      { moduleId: 'push', reason: '사용자에게 알림을 보내기 위해 필요합니다.' }
    ],
    estimatedComplexity: 'medium',
    projectSummary: '사용자의 요구사항을 반영한 모바일/웹 서비스 플랫폼입니다. 핵심 기능과 함께 확장 가능한 구조로 설계됩니다.'
  }

  // 키워드에 따른 커스터마이징
  if (hasDelivery) {
    analysis.problemStatement = '음식 배달 서비스를 통해 소비자와 음식점을 연결하는 플랫폼을 구축합니다.'
    analysis.targetUsers = '음식을 주문하려는 소비자, 배달 서비스를 제공하려는 음식점, 배달원'
    analysis.coreFeatures = [
      { id: 'feature_1', name: '메뉴 검색/주문', description: '음식점 검색, 메뉴 확인, 주문 기능' },
      { id: 'feature_2', name: '실시간 배달 추적', description: '주문 상태 및 배달원 위치 실시간 확인' },
      { id: 'feature_3', name: '결제 시스템', description: '다양한 결제 수단 지원' },
      { id: 'feature_4', name: '리뷰/평점', description: '음식점 및 메뉴 리뷰, 별점 시스템' }
    ]
    analysis.suggestedModules = [
      { moduleId: 'auth', reason: '회원 인증 및 관리를 위해 필요합니다.' },
      { moduleId: 'payment', reason: '결제 처리를 위해 필요합니다.' },
      { moduleId: 'map', reason: '배달 추적 및 음식점 위치 표시를 위해 필요합니다.' },
      { moduleId: 'push', reason: '주문 상태 알림을 위해 필요합니다.' },
      { moduleId: 'review', reason: '리뷰 및 평점 시스템을 위해 필요합니다.' },
      { moduleId: 'real_time', reason: '실시간 배달 추적을 위해 필요합니다.' }
    ]
    analysis.estimatedComplexity = 'complex'
  } else if (hasHealth) {
    analysis.problemStatement = '헬스케어 관리 플랫폼을 통해 사용자의 건강 관리를 지원합니다.'
    analysis.targetUsers = '건강 관리에 관심 있는 일반 사용자, 헬스장 회원, 트레이너'
    analysis.coreFeatures = [
      { id: 'feature_1', name: '회원 관리', description: '회원 등록, 출석 체크, 회원권 관리' },
      { id: 'feature_2', name: '예약 시스템', description: 'PT 예약, 시설 예약 기능' },
      { id: 'feature_3', name: '운동 기록', description: '운동 로그, 진행 상황 추적' }
    ]
    analysis.suggestedModules = [
      { moduleId: 'auth', reason: '회원 인증 및 관리를 위해 필요합니다.' },
      { moduleId: 'booking', reason: 'PT 및 시설 예약을 위해 필요합니다.' },
      { moduleId: 'payment', reason: '회원권 결제를 위해 필요합니다.' },
      { moduleId: 'notification', reason: '예약 알림을 위해 필요합니다.' },
      { moduleId: 'analytics', reason: '운동 통계 및 진행 상황 분석을 위해 필요합니다.' }
    ]
    analysis.estimatedComplexity = 'medium'
  } else if (hasShopping) {
    analysis.problemStatement = '온라인 쇼핑 플랫폼을 통해 상품 판매와 구매를 지원합니다.'
    analysis.targetUsers = '상품을 구매하려는 소비자, 상품을 판매하려는 판매자'
    analysis.coreFeatures = [
      { id: 'feature_1', name: '상품 목록/검색', description: '상품 카탈로그, 카테고리, 검색 기능' },
      { id: 'feature_2', name: '장바구니/주문', description: '장바구니, 주문 처리, 배송 추적' },
      { id: 'feature_3', name: '결제 시스템', description: '다양한 결제 수단 지원' }
    ]
    analysis.suggestedModules = [
      { moduleId: 'auth', reason: '회원 인증 및 관리를 위해 필요합니다.' },
      { moduleId: 'cart', reason: '장바구니 기능을 위해 필요합니다.' },
      { moduleId: 'payment', reason: '결제 처리를 위해 필요합니다.' },
      { moduleId: 'search', reason: '상품 검색을 위해 필요합니다.' },
      { moduleId: 'wishlist', reason: '찜하기 기능을 위해 필요합니다.' },
      { moduleId: 'review', reason: '상품 리뷰를 위해 필요합니다.' }
    ]
    analysis.estimatedComplexity = 'complex'
  } else if (hasBooking) {
    analysis.problemStatement = '예약 관리 플랫폼을 통해 효율적인 예약 서비스를 제공합니다.'
    analysis.targetUsers = '예약이 필요한 서비스 이용자, 예약을 관리하는 사업자'
    analysis.coreFeatures = [
      { id: 'feature_1', name: '예약 시스템', description: '날짜/시간대별 예약, 예약 관리' },
      { id: 'feature_2', name: '캘린더 뷰', description: '예약 현황 캘린더로 확인' },
      { id: 'feature_3', name: '알림 시스템', description: '예약 확인, 리마인더 알림' }
    ]
    analysis.suggestedModules = [
      { moduleId: 'auth', reason: '회원 인증 및 관리를 위해 필요합니다.' },
      { moduleId: 'booking', reason: '예약 시스템을 위해 필요합니다.' },
      { moduleId: 'notification', reason: '예약 알림을 위해 필요합니다.' },
      { moduleId: 'payment', reason: '예약금 결제를 위해 필요합니다.' }
    ]
    analysis.estimatedComplexity = 'medium'
  }

  if (hasChat) {
    analysis.suggestedModules.push({ moduleId: 'chat', reason: '실시간 채팅 기능을 위해 필요합니다.' })
  }

  if (hasPayment && !analysis.suggestedModules.find(m => m.moduleId === 'payment')) {
    analysis.suggestedModules.push({ moduleId: 'payment', reason: '결제 처리를 위해 필요합니다.' })
  }

  return analysis
}

/**
 * LLM 응답에서 JSON 파싱
 * @param {string} text - LLM 응답 텍스트
 * @returns {Object|null} 파싱된 JSON 또는 null
 */
export function parseJsonFromResponse(text) {
  try {
    // JSON 블록 찾기 (```json ... ``` 형식)
    const jsonMatch = text.match(/```json\s*([\s\S]*?)\s*```/)
    if (jsonMatch) {
      return JSON.parse(jsonMatch[1])
    }

    // 직접 JSON 파싱 시도
    const trimmed = text.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      return JSON.parse(trimmed)
    }

    // { 로 시작하는 부분 찾기
    const jsonStart = text.indexOf('{')
    const jsonEnd = text.lastIndexOf('}')
    if (jsonStart !== -1 && jsonEnd !== -1) {
      return JSON.parse(text.substring(jsonStart, jsonEnd + 1))
    }

    return null
  } catch (error) {
    console.error('[BedrockService] JSON 파싱 실패:', error)
    return null
  }
}

/**
 * Bedrock 클라이언트 리셋 (호환성을 위해 유지)
 */
export function resetBedrockClient() {
  // No-op: 클라이언트 풀 없음
}
